import fs from 'fs';

import loggerGenerator from '@travel/logger';

const logger = loggerGenerator();

export interface AssetExtractorOptions {
  /**
   * Path to the asset manifest JSON file
   */
  statsFile: string;

  /**
   * Public path for assets (e.g., '/' or CDN URL)
   */
  publicPath: string;

  /**
   * Patterns to exclude from CSS chunks (e.g., lazy-loaded page CSS, lazy-loaded component CSS)
   * @default ['pages/', 'pages-', 'component-']
   */
  excludeCssPatterns?: string[];
}

/**
 * Simple asset extractor to inject chunk assets before streaming response
 * Provides getScriptTags() and getLinkTags() from webpack asset manifest
 */
export class AssetExtractor {
  private statsFile: string;
  private publicPath: string;
  private excludeCssPatterns: string[];
  private assetManifest: any = null;

  constructor(options: AssetExtractorOptions) {
    this.statsFile = options.statsFile;
    const rawPath = options.publicPath;
    this.publicPath = rawPath && !rawPath.endsWith('/') ? `${rawPath}/` : rawPath;
    this.excludeCssPatterns = options.excludeCssPatterns || ['pages/', 'pages-', 'component-'];
    this.loadManifest();
  }

  private loadManifest(): void {
    try {
      if (fs.existsSync(this.statsFile)) {
        const manifestContent = fs.readFileSync(this.statsFile, 'utf8');
        this.assetManifest = JSON.parse(manifestContent);
      } else {
        logger.warn(`Asset manifest not found at: ${this.statsFile}`);
      }
    } catch (error) {
      logger.error('Error loading asset manifest:', error);
    }
  }

  /**
   * Get CSS link tags (similar to ChunkExtractor.getStyleTags())
   * @param includeChunks - Optional array of chunk names to include (e.g., ['pages/Top', 'pages/HotelList'])
   */
  public getLinkTags(includeChunks?: string[]): string {
    if (!this.assetManifest?.files) {
      return '';
    }

    const files = this.assetManifest.files;

    // Shared CSS: use entrypoints array instead of files to preserve the CSS
    // injection order determined by webpack, including any sorting applied in
    // the manifest plugin configuration.
    const sharedCssFiles: string[] = (this.assetManifest.entrypoints ?? []).filter((file: string) =>
      file.endsWith('.css'),
    );

    // Separate remaining CSS into shared component chunks and page-specific chunks
    // to ensure correct loading order: shared → shared chunks → page CSS
    const sharedChunkCssFiles: string[] = [];
    const pageCssFiles: string[] = [];

    for (const [key, value] of Object.entries(files)) {
      if (typeof value === 'string' && value.endsWith('.css')) {
        if (sharedCssFiles.includes(value)) continue;

        const isExplicitlyIncluded =
          includeChunks && includeChunks.some((chunk) => key.includes(chunk));
        const shouldExclude = this.excludeCssPatterns.some((pattern) => value.includes(pattern));

        if (isExplicitlyIncluded) {
          // Page-specific CSS: explicitly included chunks
          // These must load LAST to override shared styles
          pageCssFiles.push(value);
        } else if (!shouldExclude) {
          // Shared component chunks: non-excluded chunks
          // These must load BEFORE page-specific CSS
          sharedChunkCssFiles.push(value);
        }
      }
    }

    return [...sharedCssFiles, ...sharedChunkCssFiles, ...pageCssFiles]
      .map((file) => `<link rel="stylesheet" href="${this.publicPath}${file}" />`)
      .join('\n');
  }

  /**
   * Get script tags (similar to ChunkExtractor.getScriptTags())
   * @param includeChunks - Optional array of chunk names to include (e.g., ['pages/Top'])
   */
  public getScriptTags(includeChunks?: string[]): string {
    if (!this.assetManifest?.files) {
      return '';
    }

    const files = this.assetManifest.files;
    const jsFiles: string[] = [];

    // Add entrypoint scripts
    if (this.assetManifest.entrypoints) {
      for (const entrypoint of this.assetManifest.entrypoints) {
        if (typeof entrypoint === 'string' && entrypoint.endsWith('.js')) {
          jsFiles.push(entrypoint);
        }
      }
    }

    // Add explicitly included chunks (e.g., current page)
    if (includeChunks) {
      for (const [key, value] of Object.entries(files)) {
        if (typeof value === 'string' && value.endsWith('.js')) {
          const isIncluded = includeChunks.some((chunk) => key.includes(chunk));
          if (isIncluded && !jsFiles.includes(value)) {
            jsFiles.push(value);
          }
        }
      }
    }

    return jsFiles
      .map((file) => `<script src="${this.publicPath}${file}" defer></script>`)
      .join('\n');
  }
}
